/* eslint-disable no-console */
import React, { useMemo, useState } from 'react';
import Logo from 'assets/images/logo-conlicitacao-dark.png';
import { useHistory } from 'react-router-dom';
import { clearRegister } from 'store/modules/register_store/actions';
import { RegisterState } from 'store/modules/register_store/types';
import { ApplicationState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { register } from 'store/modules/profile_store/actions';
import { buildTracking, campaignParamsToQueryString } from 'services/tracking_params';
import { AllStatesInitials } from 'services/regions';
import { splitBrPhone } from 'utils/phone';
import Step1 from './steps/Step1';
import Step2 from './steps/Step2';
import Step3 from './steps/Step3';

import { Container, Sidebar, Content } from './styles';
import type { OnboardingFormState, Step1Values, Step2Values, Step3Values } from './types';

const parseStates = (csv?: string) =>
  (csv ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

export const OnboardingWizard = () => {
  const [step, setStep] = useState(1);
  const dispatch = useDispatch();
  const history = useHistory();
  const [loadingFinish, setLoadingFinish] = useState(false);

  const { company, draft } = useSelector<ApplicationState, RegisterState>(
    (state) => state.session.register,
  );

  const hasDraft = Boolean(
    draft?.cnpj && draft?.email && draft?.fullName && draft?.telephone && draft?.password,
  );

  if (!hasDraft) {
    window.location.href = '/cadastro_novo';
  }

  const initialStates = '';

  // O DDD sai do valor sem o código do país: um `draft.telephone` com "+55" faria
  // o 55 virar DDD e empurraria o número, perdendo os dígitos finais.
  const phone = splitBrPhone(draft?.telephone);

  const initialForm: OnboardingFormState = useMemo(
    () => ({
      client: {
        cnpj: draft?.cnpj,
        url_params: campaignParamsToQueryString(),
      },
      contact: {
        cargo_id: '',
        ddd_celular: phone.ddd,
        email: draft?.email,
        nome: draft?.fullName,
        numero_celular: phone.numero,
        senha: draft?.password,
      },
      others: {
        employee_quantity: '',
        average_participation_bidding: '',
        billing_value: '',
      },
      step2: {
        scope: parseStates(initialStates).length > 0 ? 'regional' : 'nacional',
        servicesText: '',
        selectedStateInitials:
          parseStates(initialStates).length > 0 ? parseStates(initialStates) : AllStatesInitials,
      },
      step3: {
        goals: [],
      },
    }),
    [draft],
  );

  const [form, setForm] = useState<OnboardingFormState>(initialForm);

  const goNext = () => setStep((s) => Math.min(3, s + 1));
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const updateStep1 = (data: Step1Values) => {
    setForm((prev) => ({
      ...prev,
      contact: {
        ...prev.contact,
        cargo_id: data.cargo_id,
      },
      others: {
        ...prev.others,
        employee_quantity: data.employee_quantity,
        average_participation_bidding: data.average_participation_bidding,
        billing_value: data.billing_value,
      },
    }));
  };

  const updateStep2 = (data: Step2Values) => setForm((prev) => ({ ...prev, step2: data }));
  const updateStep3 = (data: Step3Values) => setForm((prev) => ({ ...prev, step3: data }));

  const finishOnboarding = async () => {
    if (loadingFinish) return;

    setLoadingFinish(true);

    const params = {
      site_key: 'cadastro_cnl_novo',
      conversion_identifier: 'cadastro-novo-concluido',
      client: form.client,
      contact: {
        ...form.contact,
        cargo_id: Number(form.contact.cargo_id),
      },
      others: form.others,

      described: {
        activities: form.step2.servicesText.trim(),
      },
      filter_options: {
        states_initials: form.step2.selectedStateInitials.join(','),
      },
      tools: form.step3.goals,
      new_register: true,
      tracking: buildTracking(),
    };

    try {
      await dispatch(register(params));
      const isBeginner = Boolean(company?.beginner);
      sessionStorage.setItem('register_preparing_email', draft?.email ?? '');
      sessionStorage.setItem('register_preparing_password', draft?.password ?? '');
      history.replace(isBeginner ? 'register/pending-approval' : '/register/preparing');
      dispatch(clearRegister());
    } catch (err) {
      console.log(err);
      setLoadingFinish(false);
    }
  };

  return (
    <Container>
      <Sidebar>
        <div className="company-card text-center">
          <strong>{company?.razao_social}</strong>
          <span>{draft?.cnpj}</span>
        </div>

        <div className="text-center">
          <ul className="steps text-center">
            <li className={step === 1 ? 'active' : ''}>
              <span>1.</span> Dados iniciais
            </li>
            <li className={step === 2 ? 'active' : ''}>
              <span>2.</span> Configuração
            </li>
            <li className={step === 3 ? 'active' : ''}>
              <span>3.</span> Objetivos
            </li>
          </ul>
        </div>

        <div className="sidebar-footer text-center">
          <img src={Logo} alt="ConLicitação" />
        </div>
      </Sidebar>
      <Content>
        {step === 1 && (
          <Step1
            onNext={goNext}
            values={{
              cargo_id: form.contact.cargo_id,
              employee_quantity: form.others.employee_quantity,
              average_participation_bidding: form.others.average_participation_bidding,
              billing_value: form.others.billing_value,
            }}
            onChange={updateStep1}
          />
        )}

        {step === 2 && (
          <Step2 onNext={goNext} onBack={goBack} values={form.step2} onChange={updateStep2} />
        )}

        {step === 3 && (
          <Step3
            onBack={goBack}
            onFinish={finishOnboarding}
            loadingFinish={loadingFinish}
            values={form.step3}
            onChange={updateStep3}
          />
        )}
      </Content>
    </Container>
  );
};
